// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { FormTextField } from '@/components/form-text-field';
import { SupportSnackbar } from '@/components/support-snackbar';
import { Ticket } from '@/ticket/api/types';
import { useUpdateTicket } from '@/ticket/hooks/use-update-ticket';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { SubmitHandler, useForm } from 'react-hook-form';
import { z } from 'zod';

const formSchema = z.object({ title: z.string().min(1, 'Title is required') });

type FormData = z.infer<typeof formSchema>;

interface Props {
  isOpen: boolean;
  ticket: Ticket;
  onClose: () => void;
}

export function TicketTitleDialog(props: Props) {
  const { control, handleSubmit, reset } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { title: props.ticket.title },
  });
  const queryClient = useQueryClient();
  const updateTicketMutation = useUpdateTicket(props.ticket.key, {
    onSuccess: (data) => {
      queryClient.setQueryData(['tickets', props.ticket.key], data);
      reset({
        title: data.title,
      });
      props.onClose();
    },
  });

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    updateTicketMutation.mutate({ title: data.title.trim() });
  };

  const close = () => {
    props.onClose();
  };

  return (
    <>
      <SupportSnackbar
        isOpen={updateTicketMutation.isError}
        message="An error occurred while updating the ticket."
        onClose={updateTicketMutation.reset}
      />
      <Dialog
        fullWidth
        maxWidth="xs"
        open={props.isOpen}
        onClose={close}
        PaperProps={{
          component: 'form',
          onSubmit: handleSubmit(handleValidSubmission),
        }}
      >
        <DialogTitle>Edit title</DialogTitle>
        <DialogContent dividers>
          <FormTextField
            fullWidth
            name="title"
            label="Title"
            control={control}
            disabled={updateTicketMutation.isPending}
          />
        </DialogContent>
        <DialogActions>
          <Button
            size="small"
            onClick={close}
            disabled={updateTicketMutation.isPending}
          >
            Cancel
          </Button>
          <Button
            size="small"
            type="submit"
            color="secondary"
            variant="contained"
            disabled={updateTicketMutation.isPending}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
